'use client';

import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { useEvent, useUnit } from 'effector-react';
import classNames from 'classnames';
import { calculateContextMenuPosition, openContextMenuEvent } from '@nx-frontend/shared/ui/context-menu';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { WorkspaceImage } from '@nx-frontend/shared/ui/workspace-image';
import { goToSettings } from '@nx-frontend/shared/actions';
import { useChangeWorkspace, useNavigateWithCheckUnsavedChanges } from '@nx-frontend/shared/hooks';
import { useCustomizerCheckChanges } from '@nx-frontend/shared/hooks/use-customizer-check-changes';
import { useLogout } from '@nx-frontend/shared/hooks/use-logout';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $note } from '@nx-frontend/shared/store/note';
import { $isNoteFullScreen } from '@nx-frontend/shared/store/note-fullscreen';
import { $orgCoupons, loadOrgCouponsEvent } from '@nx-frontend/shared/store/org-coupons';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { loadOrgLimitsEvent, $orgLimits } from '@nx-frontend/shared/store/org-limits';
import { $orgRoleState } from '@nx-frontend/shared/store/org-role';
import { loadOrgUsageEvent, $orgUsage } from '@nx-frontend/shared/store/org-usage';
import { loadOrgUsageSummaryEvent, $orgUsageSummary } from '@nx-frontend/shared/store/org-usage-summary';
import { $organizations } from '@nx-frontend/shared/store/organizations';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import { $transcriptPanel } from '@nx-frontend/shared/store/transcript-panel';
import {
  $isBusinessTrialActive,
  $businessTrial,
  fetchTrialsEvent,
  $isTrialsLoaded,
} from '@nx-frontend/shared/store/trial';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { $user } from '@nx-frontend/shared/store/user';
import { $wpCanManageMembers, $opCanAccessOrgPanel } from '@nx-frontend/shared/store/workspace-access';
import { $workspacesAvatars } from '@nx-frontend/shared/store/workspace-avatars';
import { $workspaces } from '@nx-frontend/shared/store/workspaces';
import { showZohoChatEvent } from '@nx-frontend/shared/store/zoho-chat';
import {
  UiContextMenuTheme,
  UiContextMenuConfigPosition,
  ContextMenuItemData,
  ContextMenuItemType,
  UiContextMenuSubX,
  UiContextMenuSubY,
} from '@nx-frontend/shared/types';
import {
  PaywallPopupPayload,
  PaywallPopupPricingReason,
  PaywallPopupReason,
  PopupType,
} from '@nx-frontend/shared/types';
import { WorkspaceAvatar } from '@nx-frontend/shared/types';
import { GetOpenPayWallPopupProps } from '@nx-frontend/shared/types';
import { UnleashFeatures } from '@nx-frontend/shared/types';
import {
  NextAppRouterPushFunction,
  NextRouterPushFunction,
  NextRouterType,
  getPaywallTitle,
  routerPush,
  getBoxFileUrl,
  getFirstLetters,
} from '@nx-frontend/shared/utils';
import { getOrgDashboardUrl } from '@nx-frontend/shared/utils/org-dashboard';
import { getOpenInvitePopup, goToAppsumoBundlePurchase } from '@nx-frontend/widgets/popups';
import { TranscriptPanelHideBtn } from '@nx-frontend/widgets/transcript-panel';
import { NavbarButton } from './navbar-button';
import { getButtonsLowerBlock, getButtonsUpperBlock, getMenuUserButton, useOpenNavbarPlusMenu } from './navbar-data';

interface NavbarProps {
  path: string;
  routerType: NextRouterType;
  routerPushFn: NextRouterPushFunction | NextAppRouterPushFunction;
  getOpenPaywallPopup: (props: GetOpenPayWallPopupProps) => void;
}

export function Navbar({ path, routerType = NextRouterType.PAGE, routerPushFn, getOpenPaywallPopup }: NavbarProps) {
  const [
    user,
    currentOrganization,
    workspacesAvatars,
    organizations,
    currentWorkspace,
    role,
    currentNote,
    isBusinessTrialActive,
    orgCoupons,
    billingInfo,
    orgInfo,
    isNoteFullScreen,
    canManageMembers,
    businessTrial,
    orgUsageSummary,
    orgUsage,
    isTrialsLoaded,
    unleashFlags,
    orgLimits,
    transcriptPanel,
    opCanAccessOrgPanel,
    portalSettings,
    workspaces,
  ] = useUnit([
    $user,
    $currentOrganization,
    $workspacesAvatars,
    $organizations,
    $currentWorkspace,
    $orgRoleState,
    $note,
    $isBusinessTrialActive,
    $orgCoupons,
    $billingInfo,
    $orgInfo,
    $isNoteFullScreen,
    $wpCanManageMembers,
    $businessTrial,
    $orgUsageSummary,
    $orgUsage,
    $isTrialsLoaded,
    $unleashFlags,
    $orgLimits,
    $transcriptPanel,
    $opCanAccessOrgPanel,
    $portalSettings,
    $workspaces,
  ]);

  const [openModal, loadOrgCoupons, loadOrgUsageSummary, loadOrgUsage, fetchTrials, showZohoChat, loadOrgLimits] =
    useUnit([
      openModalEvent,
      loadOrgCouponsEvent,
      loadOrgUsageSummaryEvent,
      loadOrgUsageEvent,
      fetchTrialsEvent,
      showZohoChatEvent,
      loadOrgLimitsEvent,
    ]);

  const unexpectedErrorToastShown = useRef(false);
  const { toast } = useToast();

  const navigateParams = useMemo(() => {
    return {
      routerPush: (url: string) => routerPush(url, routerType, routerPushFn),
    };
  }, [routerType, routerPushFn]);
  const navigateWithCheckUnsavedChanges = useNavigateWithCheckUnsavedChanges(navigateParams);
  const changeWorkspace = useChangeWorkspace();
  const logout = useLogout();

  const customizerCheckChanges = useCustomizerCheckChanges();
  const pathname = usePathname();
  const openNavbarPlusMenu = useOpenNavbarPlusMenu();
  const [showMoreBtn, setShowMoreBtn] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [maxVisibleBtns, setMaxVisibleBtns] = useState(10);
  const workspaceListAvailable = unleashFlags[UnleashFeatures.SHOW_NAVBAR_WORKSPACES];

  // TODO: move async loading to other place, possibly to special component in the layout that doing only lazy loading.
  useEffect(() => {
    if (!orgCoupons.isLoaded) {
      // lazy load coupons
      loadOrgCoupons(currentOrganization);
    }
    if (!orgUsageSummary.isLoaded && !orgUsageSummary.isFailed) {
      // lazy load coupons
      loadOrgUsageSummary(currentOrganization);
    }
    if (!orgUsage.isLoaded) {
      // lazy load usage
      loadOrgUsage(currentOrganization);
    }
    if (!isTrialsLoaded) {
      fetchTrials();
    }
    if (orgCoupons.isLoaded && isTrialsLoaded) {
      setIsLoaded(true);
    }
    if (!orgLimits.isLoaded) {
      loadOrgLimits(currentOrganization);
    }
  }, [
    currentOrganization,
    orgCoupons.isLoaded,
    loadOrgCoupons,
    orgUsageSummary,
    loadOrgUsageSummary,
    orgUsage,
    loadOrgUsage,
    isTrialsLoaded,
    fetchTrials,
    loadOrgLimits,
    orgLimits.isLoaded,
  ]);

  const tGlobal = useTranslations();
  const router = useRouter();

  useEffect(() => {
    if (orgUsageSummary.isFailed) {
      if (unexpectedErrorToastShown.current) {
        return;
      }
      unexpectedErrorToastShown.current = true;
      toast({
        title: tGlobal('webnotes.webnotes-unexpected_error'),
      });
    }
  }, [orgUsageSummary.isFailed, tGlobal, toast]);

  const workspacesAvatarsByGlobalId = useMemo(() => {
    return workspacesAvatars.reduce((acc, avatar) => {
      acc[avatar.workspaceId] = avatar;
      return acc;
    }, {} as Record<string, WorkspaceAvatar>);
  }, [workspacesAvatars]);

  const openContextMenu = useEvent(openContextMenuEvent);

  const topButtonsRef = useRef<HTMLDivElement>(null);
  const topButtonsWrapperRef = useRef<HTMLDivElement>(null);

  const buttonsUpperBlock = getButtonsUpperBlock(
    tGlobal,
    currentOrganization,
    role,
    pathname,
    currentWorkspace.globalId,
    portalSettings.id,
    workspaces,
    workspacesAvatarsByGlobalId,
    maxVisibleBtns,
    workspaceListAvailable
  );

  useEffect(() => {
    let resizeObserver: null | ResizeObserver = null;
    if (workspaceListAvailable) {
      resizeObserver = new ResizeObserver(() => {
        const topButtonsRect = topButtonsRef.current?.getBoundingClientRect();
        const topButtonsWrapperRect = topButtonsWrapperRef.current?.getBoundingClientRect();

        if (topButtonsRect && topButtonsWrapperRect) {
          const btnHeight = 32;
          const btnGap = 16;

          const maxBtns = Math.floor((topButtonsWrapperRect.height - btnHeight - btnGap) / (btnHeight + btnGap));
          setMaxVisibleBtns(maxBtns);
          setShowMoreBtn(maxBtns < buttonsUpperBlock.length);
        }
      });

      if (topButtonsWrapperRef.current) {
        resizeObserver.observe(topButtonsWrapperRef.current);
      }
    }

    return () => {
      if (workspaceListAvailable) {
        resizeObserver?.disconnect();
      }
    };
  }, [buttonsUpperBlock.length, workspaceListAvailable]);

  const handleButtonClick = (id: string) => (e: MouseEvent) => {
    const target = e.currentTarget;

    if (id === 'plus') {
      e.preventDefault();
      customizerCheckChanges(() => {
        openNavbarPlusMenu(target);
      });
    }

    if (id === 'org' && unleashFlags[UnleashFeatures.DASHBOARD] && user.id === currentOrganization.userId) {
      customizerCheckChanges(() => {
        navigateWithCheckUnsavedChanges(
          getOrgDashboardUrl({ orgId: currentOrganization.globalId, spaceId: currentWorkspace.globalId })
        );
      });
    }

    if (id === 'user') {
      e.preventDefault();
      customizerCheckChanges(() => {
        openContextMenu({
          items: menuDataUserButton,
          position: calculateContextMenuPosition(target, 0, UiContextMenuConfigPosition.BOTTOM_RIGHT),
          // additionalStyles: 'max-h-[450px] overflow-y-auto overflow-x-hidden',
          config: {
            theme: UiContextMenuTheme.LIGHT,
          },
        });
      });
    }

    if (id === 'settings') {
      customizerCheckChanges(() => {
        goToSettings({ routerType, routerPushFn }, '/org/' + currentOrganization.globalId);
      });
    }

    if (id === 'automation') {
      customizerCheckChanges(() => {
        navigateWithCheckUnsavedChanges(`/space/automation`);
      });
    }

    if (id === 'ai-agents') {
      customizerCheckChanges(() => {
        navigateWithCheckUnsavedChanges(`/space/${currentWorkspace.globalId}/agents`);
      });
    }

    if (id === 'academy') {
      window.open('https://fusebase-academy.p.nimbusweb.me/', '_blank');
    }

    if (id.startsWith('workspace_')) {
      const [, workspaceId] = id.split('_');
      if (workspaceId) {
        handleChangeWorkspace(workspaceId);
      }
    }

    if (id === 'invite') {
      customizerCheckChanges(() => {
        if (currentWorkspace && currentWorkspace.globalId) {
          getOpenInvitePopup({ orgInfo, role, billingInfo, currentWorkspace, canManageMembers }, openModal, {
            routerType,
            routerPushFn,
          })({
            payloadType: PopupType.INVITE,
            subjectType: 'workspace',
          });
        } else {
          goToSettings({ routerType, routerPushFn }, '/org/' + currentOrganization.globalId + '/members/invite');
        }
      });
    }

    if (id === 'upgrade' || id === 'upgrade-trial') {
      const paywallPopupPayload: PaywallPopupPayload = {
        payloadType: PopupType.PAYWALL,
        title: getPaywallTitle(orgInfo.subscriptionType, tGlobal),
        reason: PaywallPopupReason.BOTTOM_NAVBAR_UPGRADE,
        pricingReason: PaywallPopupPricingReason.MENU_UPGRADE_TO_PRO,
        orgId: currentOrganization.globalId,
      };
      getOpenPaywallPopup({ openModal, payload: paywallPopupPayload });
    }
  };

  const handleChangeWorkspace = useCallback(
    (workspaceId: string) => {
      customizerCheckChanges(() => {
        if (workspaceId !== currentWorkspace.globalId) {
          changeWorkspace(workspaceId);
        }
        router.push(`/space/${workspaceId}/all`);
      });
    },
    [changeWorkspace, currentWorkspace.globalId, customizerCheckChanges, router]
  );

  const buttonsLowerBlock = getButtonsLowerBlock(
    tGlobal,
    currentOrganization,
    user,
    role,
    path,
    orgCoupons,
    billingInfo,
    orgInfo,
    isBusinessTrialActive,
    businessTrial,
    isLoaded,
    typeof window !== 'undefined' ? window.location.pathname : '',
    unleashFlags,
    opCanAccessOrgPanel
  );
  const menuDataUserButton = getMenuUserButton(
    tGlobal,
    currentOrganization,
    organizations,
    { routerType, routerPushFn },
    currentWorkspace?.globalId,
    currentNote?.globalId,
    openModal,
    orgCoupons,
    billingInfo,
    orgInfo,
    user,
    businessTrial,
    typeof window !== 'undefined' ? window.location.pathname : '',
    showZohoChat,
    getOpenPaywallPopup,
    goToAppsumoBundlePurchase,
    logout,
    user.id
  ).filter((item) => {
    if (unleashFlags[UnleashFeatures.USER_MENU_WHITE_LABEL]) {
      return !['chat-with-us', 'fusebase-helper', 'get-expert-demo', 'switch-to-old-client'].includes(item.id || '');
    }
    return true;
  });

  const handleMoreBtnClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();

      const moreMenuItems: ContextMenuItemData[] = buttonsUpperBlock
        .filter((item, index) => item.hidden)
        .map((item) => {
          const [, workspaceId] = item.id?.split('_') || [];
          let wsLetters: string | null = null;
          let wsAvatarUrl: string | null = null;
          const workspace = workspaces.find((workspace) => workspace.globalId === workspaceId);
          if (workspace) {
            const workspaceAvatar: WorkspaceAvatar | undefined = workspacesAvatarsByGlobalId[workspaceId];
            wsAvatarUrl = workspaceAvatar?.storedFileUUID ? getBoxFileUrl(workspaceAvatar?.storedFileUUID) : null;
            wsLetters = getFirstLetters(workspace.title)?.toUpperCase();
          }

          return {
            id: item.id,
            title: item.tooltipContent ?? '',
            type: ContextMenuItemType.HTML,
            html: (
              <div className="flex items-center gap-2">
                <div className="w-6 h-6">
                  {wsAvatarUrl ? (
                    <WorkspaceImage image={wsAvatarUrl} />
                  ) : (
                    <div
                      className={classNames(
                        'w-6 h-6 bg-white border border-neutral-200 rounded flex items-center justify-center text-xs'
                      )}
                    >
                      {wsLetters}
                    </div>
                  )}
                </div>
                <div>{workspace?.title}</div>
              </div>
            ),
            action: () => {
              const [, workspaceId] = item.id.split('_');
              handleChangeWorkspace(workspaceId);
            },
          };
        });

      /**
       * In case if menu large than window height, we need to move it up
       */
      const menuHeight = 32 * moreMenuItems.length + 10;
      const position = calculateContextMenuPosition(e.currentTarget, 0, UiContextMenuConfigPosition.TOP_RIGHT);
      const y = window.innerHeight - menuHeight;

      if (position.y + menuHeight > window.innerHeight) {
        position.y = y > 0 ? y : 20;
      }

      openContextMenu({
        items: moreMenuItems,
        position: {
          ...position,
          subX: UiContextMenuSubX.TOP,
          subY: UiContextMenuSubY.LEFT,
        },
        config: {
          theme: UiContextMenuTheme.LIGHT,
        },
        additionalStyles: y < 0 ? 'context-menu_overflow' : '',
      });
    },
    [buttonsUpperBlock, handleChangeWorkspace, openContextMenu, workspaces, workspacesAvatarsByGlobalId]
  );

  return (
    <div
      data-cy="webnotes-navbar"
      className={classNames(
        'webnotes-navbar flex flex-col grow-0 w-navbar box-border bg-white border-r border-neutral-200 relative z-[5] shrink-0 items-center pt-3.5 pb-2',
        {
          hidden: isNoteFullScreen,
        }
      )}
    >
      <div className="w-full h-full flex flex-col justify-between">
        <div className={classNames('grow min-h-[150px]')} ref={topButtonsWrapperRef}>
          <div className="flex flex-col gap-1 items-center justify-start " ref={topButtonsRef}>
            {buttonsUpperBlock.map((button) => {
              if (button.hidden) {
                return null;
              }
              return (
                <NavbarButton
                  key={button.id}
                  icon={button.icon}
                  buttonID={button.id}
                  image={button.image}
                  tooltipContent={button.tooltipContent}
                  isSelected={button.isSelected}
                  onClick={handleButtonClick}
                  orgButtonActive={unleashFlags[UnleashFeatures.DASHBOARD] && user.id === currentOrganization.userId}
                />
              );
            })}
            {showMoreBtn ? (
              <button
                className="flex justify-center items-center relative w-full cursor-default h-[32px]"
                onClick={handleMoreBtnClick}
              >
                <div className="flex justify-center items-center relative w-8 h-8 hover:bg-neutral-100 hover:rounded cursor-pointer">
                  <Icon icon={IconsMap.MORE_HORIZONTAL} className="w-[20x] h-[20px] text-neutral-500" />
                </div>
              </button>
            ) : null}
          </div>
        </div>
        <div className="flex flex-col gap-1 items-center justify-center pt-[20px]">
          {buttonsLowerBlock.map((button) => (
            <NavbarButton
              key={button.id}
              icon={button.icon}
              buttonID={button.id}
              image={button.image}
              tooltipContent={button.tooltipContent}
              additionalTooltipContent={button.additionalTooltipContent}
              onClick={handleButtonClick}
              isSelected={button.isSelected}
              trialDaysLeft={button.trialDaysLeft}
            />
          ))}
        </div>
      </div>
      {transcriptPanel.opened && transcriptPanel.visible ? (
        <TranscriptPanelHideBtn
          additionClass="absolute"
          style={{
            top: 'calc(50% - 12px)',
            left: `calc(55px - 12px)`,
          }}
        />
      ) : null}
    </div>
  );
}
